import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Typography,
    Box,
    TextField,
    Button,
    Avatar,
    Grid,
    IconButton,
    Stack,
    MenuItem,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Link,
} from "@mui/material";
import {
    EditOutlined,
    PhotoCamera,
    Save as SaveIcon,
    Delete as DeleteIcon,
    CloudUploadOutlined,
    NoPhotographyOutlined,
} from "@mui/icons-material";
import { useAppDispatch } from "../../../store/hook";
import { RootState } from "../../../store/store";
import {
    selectProfilePhoto,
    selectUserLocation,
    setLocation,
    setStatus,
    uploadProfilePhoto,
    fetchProfilePhoto,
    deleteProfilePhoto,
    updateProfile,
    setFirstName,
    setLastName,
    setTitle,
    setAbout,
    setLinkedinURL,
} from "../../../store/slices/profileSlice";
import { setUserLinkedinURL } from "../../../store/slices/authSlice";
import classes from "../Settings.module.scss";

const ProfileInfoTab: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user, token } = useSelector((state: RootState) => state.auth);
    const profileData = useSelector((state: RootState) => state.profile);
    const profilePhoto = useSelector(selectProfilePhoto);
    const location = useSelector(selectUserLocation);

    // Local states
    const [isEditingBasic, setIsEditingBasic] = useState(false);
    const [isEditingLocation, setIsEditingLocation] = useState(false);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [isEditPhotoModalOpen, setIsEditPhotoModalOpen] = useState(false);

    // Form states
    const [basicInfo, setBasicInfo] = useState({
        firstName: profileData.firstName || user?.firstName || "",
        lastName: profileData.lastName || user?.lastName || "",
        title: profileData.title || "Software Engineer",
        email: user?.email || "",
        linkedinURL: profileData.linkedinURL || "",
    });

    const [locationInfo, setLocationInfo] = useState({
        country: location?.country || "",
        state: location?.state || "",
        city: location?.city || "",
    });

    const [bioInfo, setBioInfo] = useState({
        bio: profileData.about || "",
    });

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Country options
    const countryOptions = [
        { value: "usa", label: "United States" },
        { value: "canada", label: "Canada" },
        { value: "uk", label: "United Kingdom" },
        { value: "australia", label: "Australia" },
    ];

    // State/Province options (simplified - would dynamically change based on country)
    const stateOptions = [
        { value: "ca", label: "California" },
        { value: "ny", label: "New York" },
        { value: "tx", label: "Texas" },
        { value: "on", label: "Ontario" },
        { value: "bc", label: "British Columbia" },
    ];

    // Update local state when Redux state changes
    useEffect(() => {
        if (user) {
            setBasicInfo((prev) => ({
                ...prev,
                firstName: profileData.firstName || user.firstName || prev.firstName,
                lastName: profileData.lastName || user.lastName || prev.lastName,
                title: profileData.title || prev.title,
                email: user.email || prev.email,
                linkedinURL: profileData.linkedinURL || prev.linkedinURL || "",
            }));
        }

        if (location) {
            setLocationInfo({
                country: location.country || "",
                state: location.state || "",
                city: location.city || "",
            });
        }

        setBioInfo({
            bio: profileData.about || "",
        });
    }, [user, location, profileData]);

    // Handle basic info form changes
    const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setBasicInfo((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle location form changes
    const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLocationInfo((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle bio form changes
    const handleBioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setBioInfo({
            bio: e.target.value,
        });
    };

    // Save basic info to Redux/API
    const saveBasicInfo = async () => {
        setIsLoading(true);
        // Update local Redux state immediately for a responsive UI feel
        dispatch(setFirstName(basicInfo.firstName));
        dispatch(setLastName(basicInfo.lastName));
        dispatch(setTitle(basicInfo.title));
        dispatch(setLinkedinURL(basicInfo.linkedinURL));
        dispatch(setUserLinkedinURL(basicInfo.linkedinURL));

        // Prepare the full profile data object for the API call
        const updatedProfile = {
            ...profileData,
            firstName: basicInfo.firstName,
            lastName: basicInfo.lastName,
            title: basicInfo.title,
            linkedinURL: basicInfo.linkedinURL,
        };

        try {
            // Dispatch the async thunk to update the profile on the backend
            const result = await dispatch(
                updateProfile({ token: token || "", profileData: updatedProfile }),
            );

            // Check if the API call was successful
            if (updateProfile.fulfilled.match(result)) {
                showSaveSuccess();
            } else {
                // Optionally handle the error case, e.g., show an error message
                console.error("Failed to update profile:", result.payload);
            }
        } catch (error) {
            console.error("An unexpected error occurred while updating the profile:", error);
        } finally {
            // Ensure loading and editing states are reset regardless of outcome
            setIsLoading(false);
            setIsEditingBasic(false);
        }
    };

    // Save location to Redux/API
    const saveLocationInfo = () => {
        setIsLoading(true);
        // Update location in Redux
        dispatch(setLocation(locationInfo));
        // Simulate API call
        setTimeout(() => {
            setIsLoading(false);
            setIsEditingLocation(false);
            showSaveSuccess();
        }, 1000);
    };

    // Save bio to Redux/API
    const saveBioInfo = async () => {
        setIsLoading(true);
        // Update about in Redux
        dispatch(setAbout(bioInfo.bio));
        // Update full profile with bio
        const updatedProfile = {
            ...profileData,
            about: bioInfo.bio,
        };
        const result = await dispatch(
            updateProfile({ token: token || "", profileData: updatedProfile }),
        );
        if (result.payload) {
            setIsLoading(false);
            setIsEditingBio(false);
            showSaveSuccess();
        } else {
            setIsLoading(false);
            setIsEditingBio(false);
        }
    };

    // Show success message temporarily
    const showSaveSuccess = () => {
        setShowSuccessMessage(true);
        setTimeout(() => {
            setShowSuccessMessage(false);
        }, 3000);
    };

    // Upload profile photo
    const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0 && token) {
            setIsUploadingPhoto(true);

            const file = files[0];
            const reader = new FileReader();
            reader.onload = (e) => {
                const photoBase64 = e.target?.result as string;

                // Upload photo using the correct parameter format
                dispatch(uploadProfilePhoto({ token, photoData: photoBase64 }))
                    .then(() => {
                        // After upload succeeds, fetch the latest photo
                        if (user && user.id) {
                            dispatch(fetchProfilePhoto(user.id));
                        }
                        setIsUploadingPhoto(false);
                        showSaveSuccess();
                    })
                    .catch(() => {
                        setIsUploadingPhoto(false);
                    });
            };
            reader.readAsDataURL(file);
        }
    };

    // Delete profile photo
    const handleDeletePhoto = () => {
        if (user?.id) {
            setIsUploadingPhoto(true);
            dispatch(deleteProfilePhoto(user.id))
                .then(() => {
                    setIsUploadingPhoto(false);
                    showSaveSuccess();
                })
                .catch(() => {
                    setIsUploadingPhoto(false);
                });
        }
    };

    const handleTriggerUpload = () => {
        fileInputRef.current?.click();
        setIsEditPhotoModalOpen(false);
    };

    const handleRemovePhotoClick = () => {
        handleDeletePhoto();
        setIsEditPhotoModalOpen(false);
    };

    return (
        <div>
            <div className={classes.contentHeader}>
                <Typography variant="h5" component="h1">
                    Profile Information
                </Typography>
                <Typography variant="body2">
                    Manage your personal information and how it appears on MAX Aspire
                </Typography>
            </div>

            {/* Profile Photo Section */}
            <div className={classes.profileSection}>
                <div className={classes.avatarContainer}>
                    <Avatar
                        src={profilePhoto || ""}
                        alt={profileData.name || "User"}
                        className={classes.avatar}
                    />
                    <Stack direction="row" spacing={1} className={classes.photoActions}>
                        <IconButton
                            className={classes.editPhotoButton}
                            onClick={() => setIsEditPhotoModalOpen(true)}
                            disabled={isUploadingPhoto}
                            aria-label="Edit profile photo"
                            sx={{
                                backgroundColor: "var(--gold-accent)",
                                color: "var(--white-color)",
                                "&:hover": {
                                    backgroundColor: "var(--gold-accent-dark)",
                                },
                                position: "absolute",
                                bottom: 8,
                                right: "50%",
                                transform: "translateX(50%)",
                                zIndex: 1,
                            }}
                        >
                            {isUploadingPhoto ? (
                                <CircularProgress size={20} sx={{ color: "white" }} />
                            ) : (
                                <EditOutlined
                                    fontSize="small"
                                    sx={{ color: "var(--primary-color)" }}
                                />
                            )}
                        </IconButton>
                        <input
                            ref={fileInputRef}
                            hidden
                            accept="image/*"
                            type="file"
                            onChange={handlePhotoUpload}
                            id="profile-photo-upload-input"
                        />
                    </Stack>
                </div>
                <div className={classes.profileInfo}>
                    <Typography variant="h6">
                        {basicInfo.firstName + " " + basicInfo.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {basicInfo.title || "Add your title"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        {location?.country && location?.state
                            ? `${location.state}, ${location.country}`
                            : "Add your location"}
                    </Typography>
                </div>
            </div>

            {/* Success Message */}
            {showSuccessMessage && (
                <Box
                    sx={{
                        p: 2,
                        mb: 2,
                        bgcolor: "#ecfdf3",
                        borderRadius: 1,
                        color: "#027a48",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <SaveIcon sx={{ mr: 1 }} />
                    <Typography variant="body2">Changes saved successfully!</Typography>
                </Box>
            )}

            {/* Edit Photo Modal */}
            <Dialog
                open={isEditPhotoModalOpen}
                onClose={() => setIsEditPhotoModalOpen(false)}
                aria-labelledby="edit-photo-dialog-title"
                PaperProps={{
                    className: classes.dialogPaper,
                }}
            >
                <DialogTitle id="edit-photo-dialog-title" className={classes.dialogTitle}>
                    Change Profile Photo
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={handleTriggerUpload}
                                className={classes.dialogListItemButton}
                            >
                                <ListItemIcon className={classes.dialogListItemIcon}>
                                    <CloudUploadOutlined />
                                </ListItemIcon>
                                <ListItemText primary="Upload New Photo" />
                            </ListItemButton>
                        </ListItem>
                        {profilePhoto && (
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={handleRemovePhotoClick}
                                    className={classes.dialogListItemButtonDanger}
                                >
                                    <ListItemIcon className={classes.dialogListItemIconDanger}>
                                        <NoPhotographyOutlined />
                                    </ListItemIcon>
                                    <ListItemText primary="Remove Current Photo" />
                                </ListItemButton>
                            </ListItem>
                        )}
                    </List>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={() => setIsEditPhotoModalOpen(false)}
                        color="primary"
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Basic Information Card */}
            <div className={classes.infoCard}>
                <div className={classes.infoHeader}>
                    <Typography variant="h6">Basic Information</Typography>
                    {!isEditingBasic && (
                        <div className={classes.editButton} onClick={() => setIsEditingBasic(true)}>
                            <EditOutlined fontSize="small" />
                            <span>Edit</span>
                        </div>
                    )}
                </div>

                {isEditingBasic ? (
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={basicInfo.firstName}
                                    onChange={handleBasicInfoChange}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={basicInfo.lastName}
                                    onChange={handleBasicInfoChange}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Professional Title"
                                    name="title"
                                    value={basicInfo.title}
                                    onChange={handleBasicInfoChange}
                                    variant="outlined"
                                    size="small"
                                    placeholder="E.g., Software Engineer, Product Manager"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="LinkedIn Profile URL"
                                    name="linkedinURL"
                                    value={basicInfo.linkedinURL}
                                    onChange={handleBasicInfoChange}
                                    variant="outlined"
                                    size="small"
                                    placeholder="https://www.linkedin.com/in/your-profile"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    value={basicInfo.email}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "var(--hover-background)",
                                        },
                                        "& .MuiInputBase-input": {
                                            color: "var(--text-secondary-color)",
                                            cursor: "default",
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "var(--border-color)",
                                        },
                                        "& .MuiFormLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                    }}
                                    helperText="Email cannot be changed here"
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={() => setIsEditingBasic(false)}
                                sx={{ mr: 1 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={saveBasicInfo}
                                disabled={isLoading}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    First Name
                                </Typography>
                                <Typography variant="body1">{basicInfo.firstName}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Last Name
                                </Typography>
                                <Typography variant="body1">{basicInfo.lastName}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Professional Title
                                </Typography>
                                <Typography variant="body1">
                                    {basicInfo.title || "Add your title"}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    LinkedIn Profile URL
                                </Typography>
                                {basicInfo.linkedinURL ? (
                                    <Link
                                        href={basicInfo.linkedinURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body1"
                                        sx={{
                                            wordBreak: "break-all",
                                            color: "var(--primary-color)",
                                            textDecoration: "none",
                                            "&:hover": {
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {basicInfo.linkedinURL}
                                    </Link>
                                ) : (
                                    <Typography variant="body1" color="textSecondary">
                                        Add your LinkedIn URL
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    Email
                                </Typography>
                                <Typography variant="body1">{basicInfo.email}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </div>

            {/* Location Information Card */}
            <div className={classes.infoCard}>
                <div className={classes.infoHeader}>
                    <Typography variant="h6">Location</Typography>
                    {!isEditingLocation && (
                        <div
                            className={classes.editButton}
                            onClick={() => setIsEditingLocation(true)}
                        >
                            <EditOutlined fontSize="small" />
                            <span>Edit</span>
                        </div>
                    )}
                </div>

                {isEditingLocation ? (
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Country"
                                    name="country"
                                    value={locationInfo.country}
                                    onChange={handleLocationChange}
                                    variant="outlined"
                                    size="small"
                                >
                                    {countryOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="State/Province"
                                    name="state"
                                    value={locationInfo.state}
                                    onChange={handleLocationChange}
                                    fullWidth
                                >
                                    {stateOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="City"
                                    name="city"
                                    value={locationInfo.city}
                                    onChange={handleLocationChange}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={() => setIsEditingLocation(false)}
                                sx={{ mr: 1 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={saveLocationInfo}
                                disabled={isLoading}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body1">
                            {locationInfo.country && locationInfo.state
                                ? `${locationInfo.state}, ${locationInfo.country}`
                                : "Add your location"}
                        </Typography>
                    </Box>
                )}
            </div>

            {/* Bio Information Card */}
            <div className={classes.infoCard}>
                <div className={classes.infoHeader}>
                    <Typography variant="h6">About</Typography>
                    {!isEditingBio && (
                        <div className={classes.editButton} onClick={() => setIsEditingBio(true)}>
                            <EditOutlined fontSize="small" />
                            <span>Edit</span>
                        </div>
                    )}
                </div>

                {isEditingBio ? (
                    <Box component="form" sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Bio"
                            name="bio"
                            value={bioInfo.bio}
                            onChange={handleBioChange}
                            variant="outlined"
                            size="small"
                            multiline
                            rows={4}
                            placeholder="Tell us about yourself"
                        />
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={() => setIsEditingBio(false)}
                                sx={{ mr: 1 }}
                            >
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={saveBioInfo} disabled={isLoading}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body1">
                            {bioInfo.bio || "Add a description about yourself."}
                        </Typography>
                    </Box>
                )}
            </div>
        </div>
    );
};

export default ProfileInfoTab;
